import { bridge } from './bridge';
import { onScreenAll } from './visible';

export type UiSnapshot = {
  route: string | null;
  businessSites: { code: string; name: string; current: boolean }[];
  fields: string[];
  actions: string[];
  arrays: { key: string; rowCount: number; columns: string[] }[];
};

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort();
}

/**
 * 학습 후보에 넣어도 되는 화면의 구조만 읽는다.
 *
 * 입력값과 행 값은 직원·금액 정보일 수 있으므로 절대 싣지 않는다. DOM 전체 HTML도
 * 저장하지 않고, 이미 에이전트용으로 표식된 필드·액션과 배열의 키/모양만 남긴다.
 */
export function snapshotUi(): UiSnapshot {
  const app = bridge();
  const state = app.pageState() ?? {};
  const arrays = Object.entries(state).flatMap(([key, value]) => {
    if (!Array.isArray(value)) return [];
    const first = value.find((row) => row && typeof row === 'object') as Record<string, unknown> | undefined;
    return [{
      key,
      rowCount: value.length,
      columns: first ? Object.keys(first).sort() : [],
    }];
  });
  const fields = Array.from(document.querySelectorAll<HTMLElement>('[data-el-field]'))
    .map((element) => (element.getAttribute('data-el-field') ?? '').split('__')[0] ?? '');
  const actions = Array.from(document.querySelectorAll<HTMLElement>('[data-el-action]'))
    .map((element) => (element.getAttribute('data-el-action') ?? '').toLowerCase());
  return {
    route: app.currentPath(),
    businessSites: app.businessSites?.() ?? [],
    fields: unique(fields),
    actions: unique(actions),
    arrays: arrays.sort((left, right) => left.key.localeCompare(right.key)),
  };
}

/**
 * 지금 화면 조건 칸(data-el-field)에 걸린 값(글자·수 값만, 40자까지). 계획이 비워 둔 칸은 화면 기본값으로 조회되는데
 * 답이 그 값을 몰라 「연차 생성 기준은 조회 자료에 제공되지 않았습니다」라고 했다(2026-10-26). **값이라 학습 후보
 * (snapshotUi)와 섞지 않는다.** 런타임은 선택지가 있는 칸만 계산에 보인다. 칸은 지금 화면에서만 찾는다(탭으로 살아 있는
 * 앞 화면의 칸이 걸리면 안 된다).
 */
export function appliedConditions(): Record<string, string> {
  const state = bridge().pageState() ?? {};
  const keys = unique(onScreenAll('[data-el-field]')
    .map((element) => (element.getAttribute('data-el-field') ?? '').split('__')[0] ?? ''));
  const found: Record<string, string> = {};
  keys.forEach((key) => {
    const value = state[key];
    if ((typeof value === 'string' || typeof value === 'number') && String(value).length <= 40) found[key] = String(value);
  });
  return found;
}
